import React, { useEffect, useRef, useState } from 'react';

import { api, logHttpError } from '../../services/api';
import type {
  AcquireSecurityOperation,
  CompleteCurrentSessionRevoke,
  ReleaseSecurityOperation,
  SecurityOperation,
} from '../../hooks/useSecurityOperationMutex';

interface SessionItem {
  id: string;
  device_label: string;
  ip_address: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  is_current: boolean;
}

interface SessionListProps {
  onCurrentSessionRevoked: CompleteCurrentSessionRevoke;
  securityOperation: SecurityOperation | null;
  acquireSecurityOperation: AcquireSecurityOperation;
  releaseSecurityOperation: ReleaseSecurityOperation;
}

const formatDate = (value: string) => new Date(value).toLocaleString('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const SessionList: React.FC<SessionListProps> = ({
  onCurrentSessionRevoked,
  securityOperation,
  acquireSecurityOperation,
  releaseSecurityOperation,
}) => {
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let active = true;
    api.get<SessionItem[]>('/security/sessions')
      .then((response) => {
        if (active) setSessions(response.data);
      })
      .catch((loadError: unknown) => {
        logHttpError('Ошибка загрузки сессий', loadError);
        if (active) setError('Не удалось загрузить активные сессии');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      mountedRef.current = false;
    };
  }, []);

  const revoke = async (session: SessionItem) => {
    if (securityOperation || revokingId !== null) return;
    const operationToken = acquireSecurityOperation('session-revoke');
    if (operationToken === null) return;

    setRevokingId(session.id);
    setError(null);
    try {
      await api.delete(`/security/sessions/${session.id}`);
      if (session.is_current) {
        onCurrentSessionRevoked(operationToken);
        return;
      }
      if (mountedRef.current) {
        setSessions((current) => current.filter((item) => item.id !== session.id));
      }
    } catch (revokeError: unknown) {
      logHttpError('Ошибка завершения сессии', revokeError);
      if (mountedRef.current) setError('Не удалось завершить сессию');
    } finally {
      if (mountedRef.current) setRevokingId(null);
      releaseSecurityOperation(operationToken);
    }
  };

  if (loading) return <p role="status" className="text-gray-500 text-sm">Загрузка сессий...</p>;

  return (
    <section aria-labelledby="active-sessions-title">
      <h4 id="active-sessions-title" className="text-lg font-bold text-white mb-4">Активные устройства</h4>
      {error && <p role="alert" className="text-red-400 text-sm mb-3">{error}</p>}
      {sessions.length === 0 ? (
        <p className="text-gray-500 text-sm py-3">Активных сессий нет</p>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => (
            <div key={session.id} className="bg-[#15171c] p-4 rounded-xl border border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-white text-sm font-bold break-words">{session.device_label}</p>
                  {session.is_current && (
                    <span className="text-xs font-bold text-green-400 bg-green-500/10 border border-green-500/20 rounded-full px-2 py-0.5">
                      Текущая сессия
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  {session.ip_address ? `IP: ${session.ip_address} · ` : ''}Активность: {formatDate(session.last_seen_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void revoke(session)}
                disabled={securityOperation !== null || revokingId !== null}
                aria-label={`${session.is_current ? 'Завершить текущую сессию' : 'Завершить сессию'} ${session.device_label}`}
                className="shrink-0 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-xl font-bold text-sm transition disabled:opacity-50"
              >
                {revokingId === session.id ? 'Завершение...' : 'Завершить'}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SessionList;
